import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useFollowTriggerPosition } from '../../hooks/useFollowTriggerPosition';
import { DROPDOWN_OPTION_IDLE, DROPDOWN_OPTION_SELECTED } from './dropdownMenuStyles';

/**
 * Custom Select (dropdown) component.
 * Note: this is NOT a native HTML <select>; it's a button + overlay menu.
 * Uses portal to render dropdown outside of overflow containers.
 * When options are loaded async, pass loading=true to show "Chargement..." (and a spinner on the right) and keep a stable height.
 */
export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** When true, shows loading state ("Chargement..." + spinner on the right) and keeps button height stable. Use while options are fetched. */
  loading?: boolean;
  /** When false, the placeholder is not shown as first option in the dropdown list (only on the button when value is empty). Default true. */
  showEmptyOptionInList?: boolean;
  error?: string;
  className?: string;
  disabled?: boolean;
  /** Extra classes for the dropdown panel (e.g. remove max-height for short lists). */
  dropdownClassName?: string;
}

const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  loading = false,
  showEmptyOptionInList = true,
  error,
  className = "",
  disabled = false,
  dropdownClassName = "",
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);
  const isEmptyValue = value === '';
  const menuOpen = isOpen && !disabled && !loading;

  const computeDropdownPosition = useCallback((rect: DOMRect) => ({
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  }), []);

  // Position before paint; null until ready → no (0,0) flash at top-left
  const dropdownPosition = useFollowTriggerPosition({
    open: menuOpen,
    triggerRef: buttonRef,
    menuRef: dropdownRef,
    computePosition: computeDropdownPosition,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInside = Boolean(selectRef.current && selectRef.current.contains(target));
      const dropdownEl = document.getElementById('select-dropdown-portal');
      const clickedInDropdown = dropdownEl && dropdownEl.contains(target);

      if (!clickedInside && !clickedInDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdownContent = menuOpen && dropdownPosition && (
    <div
      ref={dropdownRef}
      id="select-dropdown-portal"
      role="listbox"
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-paper max-h-60 overflow-auto ${dropdownClassName}`}
    >
      {showEmptyOptionInList && placeholder && (
        <div
          role="option"
          aria-selected={value === ''}
          className={`px-3 py-2.5 text-sm cursor-pointer transition-colors ${
            value === '' ? DROPDOWN_OPTION_SELECTED : 'text-gray-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-700 dark:hover:text-primary-300'
          }`}
          onClick={() => handleSelect('')}
        >
          {placeholder}
        </div>
      )}
      {options.filter((opt) => opt.value !== '').map((option) => {
        const isSelected = option.value === value;
        return (
          <div
            key={option.value}
            role="option"
            aria-selected={isSelected}
            className={`px-3 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between ${
              isSelected ? DROPDOWN_OPTION_SELECTED : DROPDOWN_OPTION_IDLE
            }`}
            onClick={() => handleSelect(option.value)}
          >
            <span className="truncate">{option.label}</span>
            {isSelected && (
              <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative" ref={selectRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`
            block w-full min-h-[2.5rem] px-3.5 py-2.5 pr-10 border rounded-xl shadow-sm text-base sm:text-sm text-left bg-white dark:bg-gray-800
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            leading-5 transition-all
            ${error
              ? 'border-red-300 text-red-900'
              : isOpen
                ? 'border-primary-500 ring-2 ring-primary-500'
                : 'border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:border-gray-300 hover:shadow-sm'
            }
          `}
        >
          <span className={`block truncate ${selectedOption && !isEmptyValue && !loading ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}`}>
            {loading ? t('common.loading') : selectedOption ? selectedOption.label : placeholder}
          </span>
        </button>

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
          ) : (
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180 text-primary-500' : 'text-gray-400'}`} />
          )}
        </div>

        {dropdownContent && createPortal(dropdownContent, document.body)}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default Select;
